import type { MediaAsset } from "@eaub/shared";
import { formatBytes } from "../lib/format";
import { CopyButton } from "./CopyButton";

interface MediaGridProps {
  assets: MediaAsset[];
  /** When provided, cards become selectable and clicking toggles selection. */
  selectedIds?: string[];
  onToggle?: (asset: MediaAsset) => void;
  /** Show copy id / copy url controls (library view). */
  showCopy?: boolean;
  onRemove?: (asset: MediaAsset) => void;
  onRename?: (asset: MediaAsset) => void;
  actionBusy?: boolean;
}

function isImage(contentType: string): boolean {
  return contentType.startsWith("image/");
}

export function MediaGrid({ assets, selectedIds, onToggle, showCopy = false, onRemove, onRename, actionBusy = false }: MediaGridProps) {
  const selectable = typeof onToggle === "function";
  const selected = new Set(selectedIds ?? []);

  if (assets.length === 0) {
    return <p className="muted">No media uploaded yet.</p>;
  }

  return (
    <ul className="media-grid">
      {assets.map((asset) => {
        const isSelected = selected.has(asset.id);
        return (
          <li
            key={asset.id}
            className={
              "media-card" +
              (selectable ? " media-card--selectable" : "") +
              (isSelected ? " media-card--selected" : "")
            }
          >
            <div className="media-card__preview">
            {selectable ? (
              <button
                type="button"
                className="media-card__select"
                aria-pressed={isSelected}
                aria-label={`Select ${asset.filename}`}
                onClick={() => onToggle?.(asset)}
              >
                <div className="media-card__thumb">
                  {isImage(asset.contentType) ? (
                    <img src={asset.url} alt={asset.filename} loading="lazy" />
                  ) : (
                    <span className="media-card__file" aria-hidden="true">
                      {asset.contentType || "file"}
                    </span>
                  )}
                  {isSelected ? <span className="media-card__check">✓</span> : null}
                </div>
              </button>
            ) : (
              <div className="media-card__thumb">
                {isImage(asset.contentType) ? (
                  <img src={asset.url} alt={asset.filename} loading="lazy" />
                ) : (
                  <span className="media-card__file" aria-hidden="true">
                    {asset.contentType || "file"}
                  </span>
                )}
              </div>
            )}

                {onRemove ? <button type="button" className="btn btn--ghost media-card__remove"
                  aria-label={`Remove ${asset.filename}`} title={`Remove ${asset.filename}`} disabled={actionBusy}
                  onClick={() => onRemove(asset)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <path d="M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6" />
                  </svg>
                </button> : null}
            {onRename ? <button type="button" className="btn btn--ghost media-card__rename"
              aria-label={`Rename ${asset.filename}`} title={`Rename ${asset.filename}`} disabled={actionBusy}
              onClick={() => onRename(asset)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5zM14 6l4 4" />
              </svg>
            </button> : null}
            </div>

            <div className="media-card__meta">
              <span className="media-card__name" title={asset.filename}>
                {asset.filename}
              </span>
              <span className="media-card__size">{formatBytes(asset.size)}</span>
            </div>

            {showCopy ? (
              <div className="media-card__actions">
                <CopyButton value={asset.id} label="Copy ID" />
                  <CopyButton value={asset.url} label="Copy URL" />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
